import React, { useEffect, useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon } from './Icons';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
// Fix Leaflet default marker icon issue in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
// Custom green icon for the forest theme
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
const cities = [{
  name: 'Orange',
  lat: 33.7879,
  lng: -117.8531
}, {
  name: 'Villa Park',
  lat: 33.8145,
  lng: -117.8131
}, {
  name: 'Anaheim Hills',
  lat: 33.8353,
  lng: -117.7384
}, {
  name: 'Tustin',
  lat: 33.7458,
  lng: -117.8262
}, {
  name: 'Santa Ana',
  lat: 33.7455,
  lng: -117.8677
}, {
  name: 'Yorba Linda',
  lat: 33.8886,
  lng: -117.8131
}, {
  name: 'Fullerton',
  lat: 33.8703,
  lng: -117.9242
}, {
  name: 'Placentia',
  lat: 33.8722,
  lng: -117.8703
}, {
  name: 'Garden Grove',
  lat: 33.7739,
  lng: -117.9414
}, {
  name: 'Irvine',
  lat: 33.6846,
  lng: -117.8265
}, {
  name: 'Costa Mesa',
  lat: 33.6411,
  lng: -117.9187
}, {
  name: 'Huntington Beach',
  lat: 33.6603,
  lng: -117.9992
}];
// Component to handle map view updates when a city is selected
const MapController = ({
  selectedCity
}: {
  selectedCity: (typeof cities)[0] | null;
}) => {
  const map = useMap();
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (selectedCity) {
      map.flyTo(L.latLng(selectedCity.lat, selectedCity.lng), 14, {
        duration: 1.5
      });
    } else {
      map.flyTo(L.latLng(33.77, -117.87), 11, {
        duration: 1.5
      });
    }
  }, [selectedCity, map]);
  return null;
};
export const ServiceAreas = () => {
  const [selectedCity, setSelectedCity] = useState<(typeof cities)[0] | null>(null);
  const markerRefs = useRef<{
    [key: string]: L.Marker | null;
  }>({});
  const handleCityClick = (city: (typeof cities)[0]) => {
    setSelectedCity(city);
    // Open popup for the selected city
    const marker = markerRefs.current[city.name];
    if (marker) {
      marker.openPopup();
    }
    // Scroll to map on mobile if needed
    const mapElement = document.getElementById('service-areas-map');
    if (mapElement && window.innerWidth < 1024) {
      const yOffset = -100; // Account for fixed header
      const y = mapElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  return <section id="service-areas-map" className="py-24 bg-forest text-white relative z-0" data-id="element-251">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-252">
        <div className="text-center mb-16" data-id="element-253">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-254">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 text-accent mb-6" data-id="element-255">
              <MapPinIcon className="w-8 h-8" data-id="element-256" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-257">
              We Serve Your Neighborhood
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-258">
              Proudly covering all of Orange County. Select a city below to see
              our service zone.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-259">
          {/* City Menu */}
          <div className="lg:col-span-5 xl:col-span-4 order-2 lg:order-1" data-id="element-260">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3" data-id="element-261">
              {cities.map((city, index) => <motion.button key={city.name} onClick={() => handleCityClick(city)} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: index * 0.05
            }} className={`border rounded-xl p-3 transition-all flex items-center justify-start gap-3 text-sm font-medium
                    ${selectedCity?.name === city.name ? 'bg-accent border-accent text-white shadow-lg shadow-accent/20 scale-[1.02]' : 'bg-white/5 border-white/10 hover:bg-white/10 text-gray-200 hover:border-white/20'}`} data-id="element-262">
                  <MapPinIcon className={`w-4 h-4 shrink-0 ${selectedCity?.name === city.name ? 'text-white' : 'text-accent'}`} data-id="element-263" />
                  <span className="truncate" data-id="element-264">{city.name}</span>
                </motion.button>)}
            </div>

            <motion.button initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.8
          }} onClick={() => setSelectedCity(null)} className={`mt-6 w-full py-3 text-sm font-bold transition-colors border rounded-xl flex items-center justify-center gap-2
                ${!selectedCity ? 'bg-white/10 border-white/20 text-white cursor-default' : 'bg-transparent border-white/10 text-gray-400 hover:text-white hover:bg-white/5'}`} data-id="element-265">
              <MapPinIcon className="w-4 h-4" data-id="element-266" />
              View All Orange County
            </motion.button>
          </div>

          {/* Map */}
          <div className="lg:col-span-7 xl:col-span-8 order-1 lg:order-2 h-[400px] lg:h-[600px] rounded-2xl overflow-hidden border-4 border-white/10 shadow-2xl relative z-10 bg-white/5" data-id="element-267">
            <MapContainer center={L.latLng(33.77, -117.87)} zoom={11} scrollWheelZoom={false} className="w-full h-full z-10" data-id="element-268">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" data-id="element-269" />
              <MapController selectedCity={selectedCity} data-id="element-270" />

              {cities.map(city => <Marker key={city.name} position={L.latLng(city.lat, city.lng)} icon={greenIcon} ref={ref => {
              markerRefs.current[city.name] = ref;
            }} eventHandlers={{
              click: () => setSelectedCity(city)
            }} data-id="element-271">
                  <Popup className="custom-popup" data-id="element-272">
                    <div className="text-center p-2 min-w-[120px]" data-id="element-273">
                      <h3 className="font-bold text-forest text-lg mb-1 leading-tight" data-id="element-274">
                        {city.name}
                      </h3>
                      <p className="text-sm text-gray-600 m-0 font-medium" data-id="element-275">
                        Service Area
                      </p>
                    </div>
                  </Popup>
                </Marker>)}
            </MapContainer>
          </div>
        </div>
      </div>
    </section>;
};